import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  TrendingUp,
  Github,
  FolderKanban,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Zap,
  Bot,
  Activity,
  Globe,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  Route,
  FlaskConical,
  CheckSquare2,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Espaces à onglets : les assistants IA et la gestion des tâches, réunis.
const SOUS_MENUS = {
  commercial: { onglets: [
    { href: "/dashboard/messaging", label: "Conversations & campagnes" },
    { href: "/dashboard/superviseur-commercial", label: "Supervision IA" },
  ] },
  taches: { onglets: [
    { href: "/dashboard/tasks", label: "Tâches" },
    { href: "/dashboard/workspace", label: "Missions" },
    { href: "/dashboard/organizer", label: "Idées & notes" },
  ] },
  ia: { onglets: [
    { href: "/dashboard/agents-permanents", label: "Agents permanents" },
    { href: "/dashboard/validations", label: "À valider" },
    { href: "/dashboard/atelier", label: "Discussions" },
    { href: "/dashboard/agent", label: "Assistant NovaForge" },
    { href: "/dashboard/sessions", label: "Sessions" },
    { href: "/dashboard/memoire", label: "Mémoire" },
    { href: "/dashboard/emails", label: "Emails" },
  ] },
};

export const CRMLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Menu par sections. Les assistants IA sont réunis avec les projets et les tâches :
  // « Tâches » et « Agents IA » ouvrent chacun un espace à onglets (voir SOUS_MENUS).
  const menuSections = [
    { titre: null, items: [
      { href: "/dashboard", icon: LayoutDashboard, label: "Cockpit dirigeant" },
    ] },
    { titre: "Projets & IA", items: [
      { href: "/dashboard/projects", icon: FolderKanban, label: "Projets" },
      { href: "/dashboard/tasks", icon: CheckSquare2, label: "Tâches", groupe: "taches" },
      { href: "/dashboard/agents-permanents", icon: Bot, label: "Agents IA", groupe: "ia" },
    ] },
    { titre: "Pilotage", items: [
      { href: "/dashboard/roadmap", icon: Route, label: "Feuille de route" },
      { href: "/dashboard/incubator", icon: FlaskConical, label: "Incubateur" },
      { href: "/dashboard/opportunities", icon: TrendingUp, label: "Opportunités" },
      { href: "/dashboard/clients", icon: Users, label: "Clients" },
      { href: "/dashboard/pub-studio", icon: Sparkles, label: "Studio publicitaire" },
      { href: "/dashboard/emailing", icon: Mail, label: "Emailing commercial" },
      { href: "/dashboard/messaging", icon: Mail, label: "Messagerie & Commercial", groupe: "commercial" },
    ] },
    { titre: "Exploitation", items: [
      { href: "/dashboard/ecosystem", icon: Github, label: "Écosystème" },
      { href: "/dashboard/infrastructure", icon: Activity, label: "Infrastructure" },
      { href: "/dashboard/sites", icon: Globe, label: "Sites & accès" },
      { href: "/dashboard/securite", icon: ShieldCheck, label: "Sécurité" },
      { href: "/dashboard/settings", icon: Settings, label: "Paramètres" },
    ] },
  ];
  const visibleSections = ["collaborator", "observer"].includes(user?.role)
    ? [{ titre: null, items: [{ href: "/dashboard/tasks", icon: CheckSquare2, label: "Mes tâches" }, { href: "/dashboard/messaging", icon: Mail, label: "Messagerie" }] }]
    : user?.role === "intern"
      ? [{ titre: "Stage", items: [
          { href: "/dashboard/projects", icon: FolderKanban, label: "Mes projets" },
          { href: "/dashboard/tasks", icon: CheckSquare2, label: "Mes tâches" },
          { href: "/dashboard/messaging", icon: Mail, label: "Messagerie" },
        ] }]
      : menuSections;
  const sousMenu = ["collaborator", "observer", "intern"].includes(user?.role) ? null
    : Object.values(SOUS_MENUS).find((g) => g.onglets.some((o) => location.pathname.startsWith(o.href)));

  const isActive = (path, groupe) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    if (groupe && SOUS_MENUS[groupe]) {
      return SOUS_MENUS[groupe].onglets.some((o) => location.pathname.startsWith(o.href));
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`crm-sidebar transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "open translate-x-0" : "-translate-x-full"
        }`}
        data-testid="crm-sidebar"
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Logo */}
          <div className="shrink-0 p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">
                Nova<span className="text-indigo-400">Forge</span>
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
            {visibleSections.map((section, i) => (
              <div key={section.titre || i} className={i ? "pt-3" : ""}>
                {section.titre && (
                  <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {section.titre}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const actif = isActive(item.href, item.groupe);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                          actif ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                        }`}
                        data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                        {actif && <ChevronRight className="w-4 h-4 ml-auto" />}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Info */}
          <div className="shrink-0 border-t border-slate-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || "Utilisateur"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {user?.email || ""}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-slate-600"
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">NovaForge</span>
          </Link>
          <div className="w-10" />
        </div>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile close button */}
      {sidebarOpen && (
        <button
          className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/* Main Content */}
      <main className="crm-content lg:ml-[280px]">
        <div className="pt-16 lg:pt-0 min-h-screen">
          {sousMenu && (
            <div className="flex max-w-full gap-1 overflow-x-auto border-b border-slate-200 bg-white px-4 sm:px-8">
              {sousMenu.onglets.map((o) => {
                const actif = location.pathname.startsWith(o.href);
                return (
                  <Link
                    key={o.href}
                    to={o.href}
                    className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-3 text-sm sm:px-4 ${
                      actif ? "border-indigo-600 font-semibold text-indigo-600"
                        : "border-transparent font-medium text-slate-500 hover:text-indigo-600"
                    }`}
                  >
                    {o.label}
                  </Link>
                );
              })}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default CRMLayout;
